import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../components/hooks/useAuth';
import { getNotificationRules, updateNotificationRules, createTestNotification, getCenters } from '../../../services/supabaseService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Badge from '../../../components/ui/Badge';

const NotificationsQA = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState('');
  const [centers, setCenters] = useState([]);
  const [rules, setRules] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    if (userProfile?.role === 'admin') {
      fetchCenters();
    }
  }, [userProfile]);

  useEffect(() => {
    if (selectedCenter) {
      fetchRules();
    }
  }, [selectedCenter]);

  const fetchCenters = async () => {
    try {
      const data = await getCenters();
      setCenters(data || []);
      if (data?.length > 0) {
        setSelectedCenter(data?.[0]?.id);
      }
    } catch (error) {
      console.error('Error fetching centers:', error);
    }
  };

  const fetchRules = async () => {
    try {
      setLoading(true);
      const data = await getNotificationRules(selectedCenter);
      setRules(data);
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRuleChange = (field, value) => {
    setRules((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveRules = async () => {
    try {
      setSaving(true);
      if (!rules?.id) {
        alert('No rules found for this center');
        return;
      }
      await updateNotificationRules(rules?.id, rules);
      alert('Notification rules saved successfully!');
    } catch (error) {
      console.error('Error saving rules:', error);
      alert(error?.message || 'Failed to save notification rules');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestNotification = async (type) => {
    try {
      setSendingTest(true);
      const result = await createTestNotification(type, selectedCenter);
      
      if (result) {
        setTestResults((prev) => [
          ...prev,
          {
            type,
            status: 'success',
            message: 'Test notification sent successfully',
            timestamp: new Date()?.toISOString(),
          },
        ]);
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      setTestResults((prev) => [
        ...prev,
        {
          type,
          status: 'error',
          message: error?.message || 'Failed to send test notification',
          timestamp: new Date()?.toISOString(),
        },
      ]);
    } finally {
      setSendingTest(false);
    }
  };

  const notificationTypes = [
    { value: 'expiry_alert', label: 'Expiry Alert', icon: 'Calendar' },
    { value: 'vacancy_alert', label: 'Vacancy Alert', icon: 'Home' },
    { value: 'lead_sla_alert', label: 'Lead SLA Breach', icon: 'Clock' },
    { value: 'pricing_suggestion', label: 'Pricing Suggestion', icon: 'TrendingUp' },
    { value: 'churn_risk', label: 'Churn Risk', icon: 'AlertTriangle' },
  ];

  if (userProfile?.role !== 'admin') {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Test Notification Sender */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Icon name="Send" size={24} />
              Send Test Notification
            </h3>
            <p className="text-sm text-gray-600 mt-1">Verify notification delivery and real-time updates</p>
          </div>
          <Select
            value={selectedCenter}
            onChange={(e) => setSelectedCenter(e?.target?.value)}
            options={centers?.map((c) => ({ value: c?.id, label: c?.name }))}
            className="w-64"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {notificationTypes?.map((type) => (
            <div key={type?.value} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={type?.icon} size={20} className="text-blue-600" />
                <h4 className="font-semibold text-gray-900">{type?.label}</h4>
              </div>
              <Button
                onClick={() => handleSendTestNotification(type?.value)}
                loading={sendingTest}
                size="sm"
                variant="outline"
                className="w-full"
              >
                Send Test
              </Button>
            </div>
          ))}
        </div>

        {/* Test Results */}
        {testResults?.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Test Results</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {testResults?.slice()?.reverse()?.map((result, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    result?.status === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}
                >
                  <Icon
                    name={result?.status === 'success' ? 'CheckCircle' : 'XCircle'}
                    size={20}
                    className={result?.status === 'success' ? 'text-green-600' : 'text-red-600'}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{result?.type?.replace('_', ' ')}</span>
                      <Badge variant={result?.status === 'success' ? 'success' : 'danger'}>{result?.status}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{result?.message}</p>
                    <p className="text-xs text-gray-500 mt-1">{new Date(result?.timestamp)?.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Verification Checklist */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <Icon name="CheckSquare" size={20} />
            Verification Checklist
          </h4>
          <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
            <li>Notification appears in Notifications Center</li>
            <li>Mark-as-read functionality works</li>
            <li>Real-time subscription updates UI instantly</li>
            <li>Notification count badge updates correctly</li>
          </ul>
        </div>
      </Card>
      {/* Notification Rules Settings */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Icon name="Settings" size={24} />
              Notification Rules
            </h3>
            <p className="text-sm text-gray-600 mt-1">Configure notification thresholds and email toggles</p>
          </div>
          <Button onClick={handleSaveRules} loading={saving}>
            Save Rules
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : rules ? (
          <div className="space-y-6">
            {/* Expiry Alerts */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="Calendar" size={20} />
                Expiry Alerts
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Expiry Window (Days)"
                  type="number"
                  value={rules?.expiryWindowDays || 60}
                  onChange={(e) => handleRuleChange('expiryWindowDays', parseInt(e?.target?.value))}
                  description="Alert when lock-in expires within this many days"
                />
                <div className="flex items-center gap-2 mt-6">
                  <Checkbox
                    id="expiry-email"
                    checked={rules?.expiryEmailEnabled}
                    onChange={(e) => handleRuleChange('expiryEmailEnabled', e?.target?.checked)}
                  />
                  <label htmlFor="expiry-email" className="text-sm font-medium text-gray-700">
                    Enable Email Notifications
                  </label>
                </div>
              </div>
            </div>

            {/* Vacancy Alerts */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="Home" size={20} />
                Vacancy Alerts
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Vacancy Threshold (Seats)"
                  type="number"
                  value={rules?.vacancyThresholdSeats || 10}
                  onChange={(e) => handleRuleChange('vacancyThresholdSeats', parseInt(e?.target?.value))}
                  description="Alert when vacant seats exceed this number"
                />
                <div className="flex items-center gap-2 mt-6">
                  <Checkbox
                    id="vacancy-email"
                    checked={rules?.vacancyEmailEnabled}
                    onChange={(e) => handleRuleChange('vacancyEmailEnabled', e?.target?.checked)}
                  />
                  <label htmlFor="vacancy-email" className="text-sm font-medium text-gray-700">
                    Enable Email Notifications
                  </label>
                </div>
              </div>
            </div>

            {/* Lead SLA */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="Clock" size={20} />
                Lead SLA
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="SLA Time (Minutes)"
                  type="number"
                  value={rules?.leadSlaMinutes || 30}
                  onChange={(e) => handleRuleChange('leadSlaMinutes', parseInt(e?.target?.value))}
                  description="Alert when lead not contacted within this time"
                />
                <div className="flex items-center gap-2 mt-6">
                  <Checkbox
                    id="lead-sla-email"
                    checked={rules?.leadSlaEmailEnabled}
                    onChange={(e) => handleRuleChange('leadSlaEmailEnabled', e?.target?.checked)}
                  />
                  <label htmlFor="lead-sla-email" className="text-sm font-medium text-gray-700">
                    Enable Email Notifications
                  </label>
                </div>
              </div>
            </div>

            {/* Pricing Suggestions */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="TrendingUp" size={20} />
                Pricing Suggestions
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="pricing-enabled"
                    checked={rules?.pricingSuggestionEnabled}
                    onChange={(e) => handleRuleChange('pricingSuggestionEnabled', e?.target?.checked)}
                  />
                  <label htmlFor="pricing-enabled" className="text-sm font-medium text-gray-700">
                    Enable Pricing Suggestions
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="pricing-email"
                    checked={rules?.pricingEmailEnabled}
                    onChange={(e) => handleRuleChange('pricingEmailEnabled', e?.target?.checked)}
                  />
                  <label htmlFor="pricing-email" className="text-sm font-medium text-gray-700">
                    Enable Email Notifications
                  </label>
                </div>
              </div>
            </div>

            {/* Churn Risk */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="AlertTriangle" size={20} />
                Churn Risk
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="churn-enabled"
                    checked={rules?.churnRiskEnabled}
                    onChange={(e) => handleRuleChange('churnRiskEnabled', e?.target?.checked)}
                  />
                  <label htmlFor="churn-enabled" className="text-sm font-medium text-gray-700">
                    Enable Churn Risk Alerts
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="churn-email"
                    checked={rules?.churnEmailEnabled}
                    onChange={(e) => handleRuleChange('churnEmailEnabled', e?.target?.checked)}
                  />
                  <label htmlFor="churn-email" className="text-sm font-medium text-gray-700">
                    Enable Email Notifications
                  </label>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Settings" size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Rules Found</h3>
            <p className="text-gray-600">Notification rules will be created automatically for this center.</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default NotificationsQA;